import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card } from '@safetyhub/ui';
import { formatDateToLocal, type LegalDocumentPastEvidence } from '@safetyhub/shared';
import { useAuthStore } from '../../stores/auth';
import { useToastStore } from '../../stores/toast';
import { apiErrorMessage } from '../../lib/api';
import {
  downloadLegalDocumentPastEvidenceFile,
  getLegalDocumentPastRecords,
  legalDocumentsPastQueryKeys,
} from '../../lib/legalDocumentsPast';
import { EVIDENCE_KIND_LABEL } from '../../lib/legalDocumentLabels';
import { ChevronLeftIcon } from '../../components/icons';

/**
 * 계약이 끝난 현장의 **지난 기록**(L32 · 사용자 결정 2026-10-31 A안) — 링크가 닫힌 시점까지의 의무·문서를
 * 읽기 전용으로 보인다. 경계 판정은 서버(`LegalDocumentPastAccessService`)가 하고 이 화면은 그대로 그린다.
 *
 * ⛔ **쓰기 버튼을 두지 않는다** — 서버에 쓰기 입구가 없다. 상태 배지(완료·지연)도 그리지 않는다: 서버가 이 입구에서
 * 상태를 파생하지 않는다(파생은 경계 밖의 문서까지 읽는다 — shared 계약 docblock).
 * 보고서 문서는 **제목만** 보인다 — 계약이 끝난 업체에게는 그 보고서를 여는 경로가 없어 링크를 주지 않는다.
 */
export default function PastSitePage() {
  const { siteId = '' } = useParams<{ siteId: string }>();
  const companyId = useAuthStore((s) => s.currentMembership?.companyId);
  const { showToast } = useToastStore();
  const [downloading, setDownloading] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: legalDocumentsPastQueryKeys.records(companyId ?? '', siteId),
    queryFn: () => getLegalDocumentPastRecords(companyId!, siteId),
    enabled: !!companyId && !!siteId,
  });

  const download = async (evidence: LegalDocumentPastEvidence) => {
    if (!companyId || !evidence.file) return;
    setDownloading(evidence.evidenceId);
    try {
      const blob = await downloadLegalDocumentPastEvidenceFile(companyId, siteId, evidence.evidenceId);
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = evidence.file.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // 즉시 revoke하면 일부 브라우저(Firefox)가 다운로드를 취소한다 — 형제 `EvidenceListModal`과 같은 처리
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
    } catch (err) {
      showToast(apiErrorMessage(err) ?? '문서 파일을 내려받지 못했습니다.', 'error');
    } finally {
      setDownloading(null);
    }
  };

  const site = data?.site;
  const instances = data?.instances ?? [];

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 sm:px-0">
      <Link
        to="/legal-documents"
        className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-ink-500 hover:text-ink-900"
      >
        <ChevronLeftIcon className="h-4 w-4" />
        현장 목록
      </Link>

      {isLoading ? (
        <div className="flex justify-center py-12" role="status" aria-label="불러오는 중">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-600" />
        </div>
      ) : error ? (
        <div role="alert" className="rounded-card border border-rose-200 bg-rose-50 p-4">
          <p className="text-sm font-medium text-rose-700">
            {apiErrorMessage(error) ?? '지난 기록을 불러오지 못했습니다.'}
          </p>
        </div>
      ) : site ? (
        <>
          <div className="mb-6">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-[26px] font-extrabold tracking-tight text-ink-900">{site.siteName}</h1>
              <Badge tone="slate" size="sm">
                읽기 전용
              </Badge>
            </div>
            <p className="mt-1 text-[15px] font-medium text-ink-500">
              대행 기관 {site.agencyName} · 계약 종료 {formatDateToLocal(new Date(site.accessUntil))}. 그때까지의
              기록만 보이고, 수정하거나 문서를 추가할 수 없습니다.
            </p>
          </div>

          {instances.length === 0 ? (
            <Card padding="none" radius="lg" elevation="md" className="py-12 text-center">
              <p className="text-sm font-medium text-ink-900">남은 기록이 없습니다</p>
            </Card>
          ) : (
            <Card padding="none" radius="lg" elevation="md" className="overflow-hidden">
              <ul className="divide-y divide-ink-200" aria-label="지난 의무 목록">
                {instances.map((instance) => (
                  <li key={instance.instanceId} className="px-4 py-4 sm:px-6">
                    <p className="text-sm font-bold text-ink-900">
                      {instance.obligationTitle}
                      <span className="ml-2 text-xs font-medium text-ink-500">{instance.periodKey}</span>
                    </p>
                    {instance.evidences.length === 0 ? (
                      <p className="mt-1 text-xs text-ink-500">등록된 문서가 없습니다</p>
                    ) : (
                      <ul className="mt-2 space-y-2" aria-label={`${instance.obligationTitle} 문서`}>
                        {instance.evidences.map((e) => (
                          <li key={e.evidenceId} className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm text-ink-900">{EVIDENCE_KIND_LABEL[e.kind]}</p>
                              <p className="text-xs text-ink-500">
                                이행일 {formatDateToLocal(new Date(e.fulfilledAt))}
                                {e.attachedByName ? ` · ${e.attachedByName}` : ''}
                              </p>
                              {e.reportTitle && <p className="mt-1 truncate text-sm text-ink-700">{e.reportTitle}</p>}
                              {e.note && <p className="mt-1 whitespace-pre-wrap text-sm text-ink-700">{e.note}</p>}
                            </div>
                            {e.file && (
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => download(e)}
                                disabled={downloading === e.evidenceId}
                              >
                                {downloading === e.evidenceId ? '받는 중...' : e.file.filename}
                              </Button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      ) : null}
    </div>
  );
}
